import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Stethoscope, User, Moon, Sun, Briefcase } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useTheme } from '../../context/ThemeContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSiteSettings();
  const { isDarkMode, toggleTheme } = useTheme();
  const headerSettings = settings.header || {};

  const isActive = (path) => location.pathname === path;

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'user': return <User className="w-4 h-4" />;
      case 'stethoscope': return <Stethoscope className="w-4 h-4" />;
      case 'briefcase': return <Briefcase className="w-4 h-4" />;
      default: return null;
    }
  };

  const navLinks = headerSettings.navLinks?.filter(link => link.active) || [
    { id: '1', label: 'Conseils', path: '/conseils', icon: 'none' },
    { id: '2', label: 'Espace patient', path: '/espace-patient', icon: 'user' },
    { id: '3', label: 'Espace vétérinaire', path: '/espace-veterinaire', icon: 'stethoscope' },
  ];

  const logoTextPart1 = headerSettings.logoTextPart1 || 'VETO';
  const logoTextPart2 = headerSettings.logoTextPart2 || 'RDV';
  const logoType = headerSettings.logoType || 'default';
  const customLogoUrl = headerSettings.customLogoUrl || '';
  const logoColor = headerSettings.logoColor || '#22c55e';
  const logoColorDark = headerSettings.logoColorDark || '#16a34a';

  const renderLogo = () => {
    if (logoType === 'none') {
      return null;
    }
    
    if (logoType === 'custom' && customLogoUrl) {
      return (
        <img 
          src={customLogoUrl} 
          alt="Logo" 
          className="w-10 h-10 object-contain"
        />
      );
    }
    
    // Default SVG logo with custom colors
    return (
      <svg viewBox="0 0 40 40" className="w-10 h-10" fill="none">
        <path d="M20 8c-2 0-3.5 1.5-3.5 3.5S18 15 20 15s3.5-1.5 3.5-3.5S22 8 20 8z" fill={logoColor}/>
        <path d="M12 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={logoColor}/>
        <path d="M28 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={logoColor}/>
        <path d="M20 18c-6 0-10 4-10 9 0 3 2 5 5 5 2 0 3.5-1 5-3 1.5 2 3 3 5 3 3 0 5-2 5-5 0-5-4-9-10-9z" fill={logoColorDark}/>
      </svg>
    );
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 sticky top-0 z-50 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="flex items-center">
              {renderLogo()}
              <span className={`${logoType !== 'none' ? 'ml-2' : ''} text-xl font-bold`}>
                <span className="text-gray-800 dark:text-white">{logoTextPart1}</span>
                <span style={{ color: logoColor }}>{logoTextPart2}</span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.highlight ? (
                <Link
                  key={link.id}
                  to={link.path}
                  className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                  data-testid="espace-veterinaires-btn"
                >
                  {getIcon(link.icon)}
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-green-600 dark:hover:text-green-400 ${
                    isActive(link.path) 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {getIcon(link.icon)}
                  {link.label}
                </Link>
              )
            ))}
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label={isDarkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
              data-testid="theme-toggle"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label={isDarkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            <button
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 dark:border-gray-700">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                link.highlight ? (
                  <Link
                    key={link.id}
                    to={link.path}
                    className="flex items-center justify-center gap-2 font-medium px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getIcon(link.icon)}
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    key={link.id}
                    to={link.path}
                    className={`flex items-center gap-2 font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getIcon(link.icon)}
                    {link.label}
                  </Link>
                )
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
